import React, { useState, useEffect } from 'react';
import { getToken } from '../../utils/getToken';
import { NumberTo2DP } from '../../utils/NumberTo2DP';
import OverviewStat from '../overview/OverviewStat';
import OverviewBarCharts from '../../component/overview/OverviewBarCharts';
import OverviewTable from './OverviewTable';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://api.quickcare.ng/admin';

const OverviewCom = () => {
    const myDate = new Date();
    const MAX_DATE = `${myDate.getFullYear()}-${myDate.getUTCMonth() + 1 > 10 ? myDate.getUTCMonth() : `0${myDate.getUTCMonth()+1}`}-${myDate.getDate() >= 10 ? myDate.getDate() : `0${myDate.getDate()}`}`;
    const MIN_DATE = `${myDate.getFullYear()}-${myDate.getUTCMonth() + 1 > 10 ? myDate.getUTCMonth() : `0${myDate.getUTCMonth()+1}`}-${'01'}`;

    const [period, setPeriod] = useState({
        start_date: MIN_DATE,
        end_date: MAX_DATE
    });
    
    const [country, setCountry] = useState('kenya');
    const [stats, setStats] = useState({loading: true, data: {}});

    const handlePeriodChange = ({target:{name, value}}) => {
        setPeriod(prev => ({...prev, [name]:value}));
    };

    useEffect(() => {
        const fetchFinanceStats = async () => {
            const token = getToken();
            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            if (token) {
                myHeaders.append("Authorization", `Bearer ${token}`);
            }

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };
            setStats(prev => ({...prev, loading: true}));
            try {
                const response = await fetch(`${baseURL}/financials/stats?start_date=${period.start_date}&end_date=${period.end_date}&country=${country}`, requestOptions);
                const result = await response.json();
                setStats({loading: false, data:result.data});
            } catch (error) {
                setStats({loading: false, data: {}});
                console.error('Error fetching financial stats:', error);
            }
        };

        fetchFinanceStats();
    }, [period, country]);

    const getPeriodLabel = () => {
        if (typeof period === 'string') {
            switch (period) {
                case '24hr':
                    return "Today's Revenue";
                case 'all':
                    return "All Revenue";
                case '1week':
                    return "This Week Revenue";
                case '2weeks':
                    return "2 Weeks Revenue";
                case '1month':
                    return "This Month Revenue";
                case '2months':
                    return "2 Months Revenue";
                case 'quarter':
                    return "Quarter Revenue";
                case '6months':
                    return "6 Months Revenue";
                case '1year':
                    return "1 Year Revenue";
                default:
                    return "Revenue";
            }
        }
        return "Revenue";
    };

    const cardConfigs = [
        {
            id: 1,
            title: getPeriodLabel(),
            value: stats?.data?.from_month_start_amount || 0,
            icon: '💰',
            bgColor: 'bg-blue-50',
            textColor: 'text-blue-600',
            hoverBg: 'hover:bg-blue-50/80',
            borderColor: 'border-blue-100',
            isAmount: true,
            subtitle: 'Revenue from period'
        },
        {
            id: 2,
            title: `${getPeriodLabel()} Count`,
            value: stats?.data?.from_month_start_count || 0,
            icon: '📊',
            bgColor: 'bg-purple-50',
            textColor: 'text-purple-600',
            hoverBg: 'hover:bg-purple-50/80',
            borderColor: 'border-purple-100',
            isAmount: false,
            subtitle: 'Total transactions'
        },
        {
            id: 3,
            title: 'Total Subscriptions',
            value: stats?.data?.total_subscriptions_amount || 0,
            icon: '📈',
            bgColor: 'bg-green-50',
            textColor: 'text-green-600',
            hoverBg: 'hover:bg-green-50/80',
            borderColor: 'border-green-100',
            isAmount: true,
            subtitle: 'Lifetime revenue'
        },
        {
            id: 4,
            title: 'Total Count',
            value: stats?.data?.total_subscriptions_count || 0,
            icon: '👥',
            bgColor: 'bg-orange-50',
            textColor: 'text-orange-600',
            hoverBg: 'hover:bg-orange-50/80',
            borderColor: 'border-orange-100',
            isAmount: false,
            subtitle: 'Total subscribers'
        },
        {
            id: 5,
            title: "Today's Amount",
            value: stats?.data?.today_subscription_amount || 0,
            icon: '⏰',
            bgColor: 'bg-red-50',
            textColor: 'text-red-600',
            hoverBg: 'hover:bg-red-50/80',
            borderColor: 'border-red-100',
            isAmount: true,
            subtitle: 'Today\'s revenue'
        },
        {
            id: 6,
            title: "Today's Count",
            value: stats?.data?.today_subscription_count || 0,
            icon: '📅',
            bgColor: 'bg-indigo-50',
            textColor: 'text-indigo-600',
            hoverBg: 'hover:bg-indigo-50/80',
            borderColor: 'border-indigo-100',
            isAmount: false,
            subtitle: 'Today\'s transactions'
        }
    ];

    return (
        <div className='w-full px-3 sm:px-4 lg:px-6 py-4 lg:py-6 bg-gradient-to-br from-gray-50 to-gray-100/50 min-h-screen'>
            {/* Page Header */}
            <div className='mb-6'>
                <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                    <div>
                        <h1 className='text-2xl lg:text-3xl font-bold text-gray-800 flex items-center gap-2'>
                            <span>📊</span> Dashboard Overview
                        </h1>
                        <p className='text-sm text-gray-500 mt-1'>
                            Monitor your healthcare operations and financial metrics
                        </p>
                    </div>
                    <div className='flex items-center gap-2 text-sm text-gray-500 bg-white px-4 py-2 rounded-xl shadow-sm'>
                        <span className='inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse'></span>
                        Live Data
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className='bg-white rounded-2xl shadow-sm p-4 lg:p-6 mb-6 border border-gray-100 hover:shadow-md transition-shadow duration-300'>
                <div className='flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4'>
                    <div className='flex items-center gap-2'>
                        <div className='p-2 bg-[#069125]/10 rounded-xl'>
                            <span className='text-lg'>🔍</span>
                        </div>
                        <div>
                            <h2 className='text-lg font-semibold text-gray-700'>Filters</h2>
                            <p className='text-xs text-gray-400'>Customize your dashboard view</p>
                        </div>
                    </div>
                    
                    <div className='flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4 w-full lg:w-auto'>
                        <div className='flex flex-col gap-1 w-full sm:w-auto'>
                            <label className='text-xs font-medium text-gray-600 uppercase tracking-wider flex items-center gap-1'>
                                <span>📅</span> Start Date
                            </label>
                            <input 
                                className="px-3 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-[#069125]/20 focus:border-[#069125] outline-none transition-all duration-200 w-full sm:w-40 hover:border-gray-300"
                                max={period.end_date}
                                name='start_date' 
                                value={period.start_date} 
                                type='date' 
                                onChange={handlePeriodChange} 
                            />
                        </div>

                        <div className='flex flex-col gap-1 w-full sm:w-auto'>
                            <label className='text-xs font-medium text-gray-600 uppercase tracking-wider flex items-center gap-1'>
                                <span>📅</span> End Date
                            </label>
                            <input 
                                className="px-3 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-[#069125]/20 focus:border-[#069125] outline-none transition-all duration-200 w-full sm:w-40 hover:border-gray-300"
                                min={period.start_date}
                                max={MAX_DATE}
                                name='end_date' 
                                value={period.end_date} 
                                type='date' 
                                onChange={handlePeriodChange} 
                            />
                        </div>

                        <div className='flex flex-col gap-1 w-full sm:w-auto'>
                            <label className='text-xs font-medium text-gray-600 uppercase tracking-wider flex items-center gap-1'>
                                <span>🌍</span> Country
                            </label>
                            <select 
                                id="country" 
                                value={country} 
                                onChange={(e) => setCountry(e.target.value)}
                                className='px-3 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-[#069125]/20 focus:border-[#069125] outline-none transition-all duration-200 w-full sm:w-36 hover:border-gray-300 cursor-pointer'
                            >
                                <option value="kenya">🇰🇪 Kenya</option>
                                <option value="nigeria">🇳🇬 Nigeria</option>
                            </select>
                        </div>

                        {/* Apply Filters Button */}
                        <button 
                            onClick={() => {}} 
                            className='mt-4 sm:mt-6 px-6 py-2.5 bg-[#069125] text-white text-sm font-semibold rounded-xl hover:bg-[#057a1f] transition-all duration-200 hover:shadow-lg hover:shadow-[#069125]/30 w-full sm:w-auto'
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Overview Stats Component */}
            <div className='mb-6'>
                <OverviewStat period={period} country={country} />
            </div>

            {/* Subscription Cards */}
            {stats && (
                <div className='mb-8'>
                    <div className='flex items-center gap-3 mb-4'>
                        <div className='w-1 h-8 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full'></div>
                        <div>
                            <p className='text-xl font-bold text-gray-800'>Subscription Overview</p>
                            <p className='text-xs text-gray-400'>Real-time subscription metrics</p>
                        </div>
                        <span className='ml-auto text-xs bg-[#069125]/10 text-[#069125] px-3 py-1 rounded-full font-medium'>
                            {stats.loading ? '⏳ Loading...' : '✅ Live'}
                        </span>
                    </div>
                    
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4'>
                        {cardConfigs.map((card) => (
                            <div 
                                key={card.id}
                                className={`group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border ${card.borderColor} hover:border-[#069125]/30 overflow-hidden transform hover:-translate-y-1`}
                            >
                                <div className='p-4 lg:p-6'>
                                    <div className='flex items-start justify-between mb-3'>
                                        <div className={`p-2.5 rounded-xl ${card.bgColor} group-hover:scale-110 transition-transform duration-300 text-2xl`}>
                                            {card.icon}
                                        </div>
                                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${card.bgColor} ${card.textColor}`}>
                                            {card.isAmount ? '💰 Amount' : '🔢 Count'}
                                        </span>
                                    </div>
                                    
                                    <h3 className='text-sm font-medium text-gray-600 mb-1'>
                                        {card.title}
                                    </h3>
                                    
                                    <p className='text-xl lg:text-2xl font-bold text-gray-800'>
                                        {stats.loading ? (
                                            <span className='inline-block w-24 h-7 bg-gray-200 animate-pulse rounded'></span>
                                        ) : (
                                            card.isAmount ? `$${NumberTo2DP(card.value)}` : card.value.toLocaleString()
                                        )}
                                    </p>
                                    
                                    <div className='mt-3 pt-3 border-t border-gray-100 flex items-center justify-between'>
                                        <span className='text-xs text-gray-400'>
                                            {card.subtitle}
                                        </span>
                                        {!stats.loading && (
                                            <span className='text-xs text-green-500 flex items-center gap-1'>
                                                <span className='inline-block w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse'></span>
                                                Live
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Bar Chart Section */}
            <div className='w-full mb-6 p-4 lg:p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300'>
                <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4'>
                    <div className='flex items-center gap-3'>
                        <div className='w-1 h-8 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full'></div>
                        <div>
                            <p className='text-lg font-bold text-gray-800'>
                                Total Patients Per Month
                            </p>
                            <p className='text-xs text-gray-400'>Monthly patient trends</p>
                        </div>
                    </div>
                    <div className='flex items-center gap-2 mt-2 sm:mt-0'>
                        <span className='text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full flex items-center gap-1'>
                            ⏱️ Real-time data
                        </span>
                    </div>
                </div>
                
            </div>

            {/* Table Section */}
            <div className='w-full bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300 overflow-hidden'>
                <div className='p-4 lg:p-6'>
                    <div className='flex items-center justify-between mb-4'>
                        <div className='flex items-center gap-3'>
                            <div className='w-1 h-8 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full'></div>
                            <div>
                                <p className='text-lg font-bold text-gray-800'>
                                    Recent Transactions
                                </p>
                                <p className='text-xs text-gray-400'>Latest subscription activities</p>
                            </div>
                        </div>
                        <div className='flex items-center gap-2'>
                            <span className='text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full'>
                                📋 All records
                            </span>
                        </div>
                    </div>
                    <OverviewTable period={period} country={country} />
                </div>
            </div>

            <div className='w-full h-64 lg:h-80 mt-10'>
                    <OverviewBarCharts period={period} country={country} />
                </div>
        </div>
    );
};

export default OverviewCom;